import axios from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../enums/queryKeys'
import { approveImportedInvoice } from '../query-api'
import { getImportedInvoiceQueryKey } from '../utils/getImportedInvoiceQueryKey'

const UNPROCESSABLE_STATUS = 422

type ApproveImportedInvoiceResponse = Awaited<ReturnType<typeof approveImportedInvoice>>

interface UseApproveImportedInvoiceOptions {
  onError?: (error: unknown) => void
  onSuccess?: (response: ApproveImportedInvoiceResponse) => void
}

/**
 * The response is the by-id body with `approved` filled in, so it replaces the cached invoice and
 * the drawer re-renders from it without another fetch. The list is refetched because approving
 * re-projects the row's number, dates and customer.
 *
 * A 422 means the cached invoice is stale — most likely approved elsewhere — so both the invoice
 * and the list are refetched.
 */
export const useApproveImportedInvoice = ({ onError, onSuccess }: UseApproveImportedInvoiceOptions = {}) => {
  const queryClient = useQueryClient()
  const { organization } = useUserOrganization()
  const organizationId = organization?.id

  const {
    isLoading: isProcessing,
    mutate: approve,
    ...rest
  } = useMutation(approveImportedInvoice, {
    onError: (error, { importedInvoiceId }) => {
      if (axios.isAxiosError(error) && error.response?.status === UNPROCESSABLE_STATUS) {
        queryClient.invalidateQueries(getImportedInvoiceQueryKey(organizationId, importedInvoiceId))
        queryClient.invalidateQueries([QueryKeys.ImportedInvoices])
      }
      onError?.(error)
    },
    onSuccess: (response, { importedInvoiceId }) => {
      queryClient.setQueryData(getImportedInvoiceQueryKey(organizationId, importedInvoiceId), response)
      queryClient.invalidateQueries([QueryKeys.ImportedInvoices])
      onSuccess?.(response)
    },
  })

  return { ...rest, approve, isProcessing }
}
